import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Building2, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Upload, Search, GripVertical, Image as ImageIcon } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';
import ImageUploadModal from '../../components/common/ImageUploadModal';


const OrganizationsPage = () => {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Upload modal
  const [uploadModal, setUploadModal] = useState(null); // { imageType, title, aspectRatio }
  // Image preview lightbox
  const [previewImage, setPreviewImage] = useState(null); // { src, label }

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await adminManagementApi.getOrganizations(); setOrgs(res.data); }
    catch (err) { console.error('Failed to load organizations:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ID: selected.id, DisplayName: selected.displayName, Description: selected.description || '', IsActive: selected.isActive });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    setSelected(null);
    setEditing({ DisplayName: '', Description: '', IsActive: true });
    setDirtyFields(new Set(['DisplayName']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const origMap = { DisplayName: selected?.displayName, Description: selected?.description || '', IsActive: selected?.isActive };
    const newDirty = new Set(dirtyFields);
    if (value !== origMap[field] || !selected) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    setSaving(true);
    try {
      if (editing.ID) { await adminManagementApi.updateOrganization(editing.ID, editing); }
      else { await adminManagementApi.createOrganization(editing); }
      const savedId = editing.ID;
      setEditing(null); setDirtyFields(new Set());
      const res = await adminManagementApi.getOrganizations();
      setOrgs(res.data);
      if (savedId) { const refreshed = res.data.find(o => o.id === savedId); if (refreshed) setSelected(refreshed); }
      else setSelected(null);
    } catch (err) { console.error('Save failed:', err); }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    if (!editing?.ID) { cancelEdit(); return; }
    notify.confirm(`Delete organization "${editing.DisplayName}"?`, async () => {
      setSaving(true);
      try { await adminManagementApi.deleteOrganization(editing.ID); setEditing(null); setSelected(null); setDirtyFields(new Set()); await load(); }
      catch (err) { console.error('Delete failed:', err); }
      finally { setSaving(false); }
    }, { onCancel: () => cancelEdit() });
  };

  const handleUpload = async (imageType, file) => {
    const orgId = editing?.ID || selected?.id;
    if (!orgId) return;
    try {
      await adminManagementApi.uploadOrgImage(orgId, imageType, file);
      const res = await adminManagementApi.getOrganizations();
      setOrgs(res.data);
      const refreshed = res.data.find(o => o.id === orgId);
      if (refreshed) setSelected(refreshed);
      setImgCacheBust(Date.now());
      // Image is saved to blob storage immediately — exit edit mode if no other dirty fields
      if (dirtyFields.size === 0) {
        setEditing(null);
        setDirtyFields(new Set());
      }
      notify.success(`${imageType === 'logo' ? 'Logo' : 'Icon'} uploaded`);
    } catch (err) {
      const msg = err.response?.data;
      const errMsg = (typeof msg === 'string' && msg) ? msg
        : msg?.title || msg?.message || msg?.detail
        || err.message || `Failed to upload ${imageType}`;
      notify.error(errMsg);
      const e = new Error(errMsg);
      e.notified = true;
      throw e;
    }
  };

  const handleDeleteImage = (imageType) => {
    const orgId = editing?.ID || selected?.id;
    if (!orgId) return;
    notify.confirm(`Remove ${imageType}? This is immediate.`, async () => {
      try {
        await adminManagementApi.deleteOrgImage(orgId, imageType);
        const res = await adminManagementApi.getOrganizations();
        setOrgs(res.data);
        const refreshed = res.data.find(o => o.id === orgId);
        if (refreshed) setSelected(refreshed);
        setImgCacheBust(Date.now());
        if (dirtyFields.size === 0) {
          setEditing(null);
          setDirtyFields(new Set());
        }
        notify.success(`${imageType === 'logo' ? 'Logo' : 'Icon'} removed`);
      } catch (err) {
        notify.error(`Failed to delete ${imageType}`);
      }
    });
  };

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0;
  const filtered = orgs.filter(o => !searchTerm || o.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) || o.description?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Image table row renderer
  const orgId = editing?.ID || selected?.id;
  const [hoverImage, setHoverImage] = useState(null);

  const IMAGE_CONFIG = {
    icon:  { outputSize: { width: 256, height: 256 } },
    logo:  { maxOutputHeight: 80, maxOutputWidth: 400 }
  };

  const [imgCacheBust, setImgCacheBust] = useState(Date.now());
  const renderImageRow = (imageType, label, hasImage, blobUrl) => {
    const config = IMAGE_CONFIG[imageType] || {};
    const imgSrc = blobUrl ? `${blobUrl}?v=${imgCacheBust}` : null;
    const sugName = selected?.displayName ? selected.displayName.toLowerCase().replace(/[^a-z0-9]+/g, '-') + `-${imageType}` : imageType;
    return (
      <tr>
        <td className="stui-org-img-label">{label}</td>
        <td className="stui-org-img-cell"></td>
        <td className="stui-org-img-actions">
          {isEditing && !hasImage && (
            <button
              className="stui-org-upload-btn"
              onClick={() => setUploadModal({ imageType, title: `Upload ${label}`, suggestedName: sugName, ...config })}
              title={`Upload ${label}`}
            >
              <ImageIcon size={14} /><Upload size={12} />
            </button>
          )}
          {hasImage ? (
            <div
              className="stui-org-img-wrap"
              onMouseEnter={() => isEditing && setHoverImage(imageType)}
              onMouseLeave={() => setHoverImage(null)}
            >
              <img
                src={imgSrc}
                alt={label}
                onClick={() => !isEditing && setPreviewImage({ src: imgSrc, label })}
                className={`stui-org-${imageType}-img${hoverImage === imageType ? ' dimmed' : ''}${!isEditing ? ' stui-clickable' : ''}`}
              />
              {hoverImage === imageType && isEditing && (
                <button
                  className="stui-org-img-delete"
                  onClick={() => handleDeleteImage(imageType)}
                  title={`Remove ${label}`}
                >
                  <Trash2 size={20} />
                </button>
              )}
            </div>
          ) : null}
        </td>
      </tr>
    );
  };

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Building2 size={20} />
          <h1>Organizations</h1>
          <span className="stui-module-header-meta">{orgs.length} organizations</span>
        </div>
        <div className="stui-module-header-actions">
          {isEditing ? (
            <button
              className={`stui-crud-btn stui-delete-btn ${!editing?.ID ? 'disabled' : ''}`}
              onClick={editing?.ID ? handleDelete : undefined}
              disabled={saving || !editing?.ID}
              title={editing?.ID ? 'Delete' : 'Nothing to delete yet'}
            ><Trash2 size={18} /></button>
          ) : (
            <button className="stui-crud-btn" onClick={startAdd} title="Add organization"><Plus size={18} /></button>
          )}
          <button className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed"><button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button></div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search organizations..." />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">No organizations found</div>
              ) : filtered.map(org => (
                <button key={org.id} onClick={() => { setSelected(org); setEditing(null); setDirtyFields(new Set()); }} className={`stui-list-item ${selected?.id === org.id ? 'selected' : ''}`}>
                  <span className="stui-list-item-primary">{org.displayName}</span>
                  <span className="stui-list-item-secondary">{org.isActive ? 'Active' : 'Inactive'}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {(isEditing || selected) ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">{isEditing ? (editing.ID ? 'Edit Organization' : 'New Organization') : 'Organization Details'}</div></div>
                <div className="stui-field-group stui-col-8">
                  <label>Display Name</label>
                  {isEditing ? (
                    <input className={`stui-field-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} autoFocus />
                  ) : (
                    <input className="stui-field-input" value={selected.displayName} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-12">
                  <label>Description</label>
                  {isEditing ? (
                    <input className={`stui-field-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} />
                  ) : (
                    <input className="stui-field-input" value={selected.description || ''} readOnly />
                  )}
                </div>
                <div className="stui-field-group stui-col-4">
                  <label>Active</label>
                  {isEditing ? (
                    <select className={`stui-field-input${dirtyFields.has('IsActive') ? ' dirty' : ''}`} value={editing.IsActive ? 'true' : 'false'} onChange={e => handleFieldChange('IsActive', e.target.value === 'true')}>
                      <option value="true">Yes</option><option value="false">No</option>
                    </select>
                  ) : (
                    <input className="stui-field-input" value={selected.isActive ? 'Yes' : 'No'} readOnly />
                  )}
                </div>
              </div>

              {/* Images table — only available after record is saved */}
              {!orgId && isEditing && !editing.ID && (
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Images</div></div>
                  <div className="stui-col-24">
                    <p className="stui-hint">Save the organization first, then edit to add logo and icon images.</p>
                  </div>
                </div>
              )}
              {orgId && (
                <div className="stui-detail-grid">
                  <div className="stui-col-24"><div className="stui-detail-section-header">Images</div></div>
                  <div className="stui-col-24">
                    <table className="stui-org-img-table">
                      <tbody>
                        {renderImageRow('logo', 'Logo', !!selected?.logoUrl, selected?.logoUrl)}
                        {renderImageRow('icon', 'Icon', !!selected?.iconUrl, selected?.iconUrl)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="stui-detail-empty"><Eye size={48} /><p>Select an organization to view details</p><p className="stui-hint">Or click + to create a new one</p></div>
          )}
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={!!uploadModal}
        onClose={() => setUploadModal(null)}
        onUpload={async (file) => { await handleUpload(uploadModal.imageType, file); }}
        title={uploadModal?.title || 'Upload Image'}
        suggestedName={uploadModal?.suggestedName || ''}
        aspectRatio={uploadModal?.aspectRatio}
        outputSize={uploadModal?.outputSize}
        maxOutputHeight={uploadModal?.maxOutputHeight}
        maxOutputWidth={uploadModal?.maxOutputWidth}
      />

      {/* Image Preview Lightbox */}
      {previewImage && (
        <div className="stui-lightbox-overlay" onClick={() => setPreviewImage(null)}>
          <div className="stui-lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={previewImage.src} alt={previewImage.label} className="stui-lightbox-img" />
            <div className="stui-lightbox-label">{previewImage.label} — actual size</div>
            <button className="stui-lightbox-close" onClick={() => setPreviewImage(null)}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;
